import { CaretDown, ClockCounterClockwise, Play } from "@phosphor-icons/react";
import { clsx } from "clsx";
import { useState } from "react";

import CampaignTypeIcon from "common/components/CampaignTypeIcon";
import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import { CAMPAIGN_TYPES } from "common/constants";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { CAMPAIGN_OUTREACH_OPTIONS } from "../../../constants";
import { useCampaignContext } from "../../../context/CampaignContext";
import useCampaignValidation from "../../../hooks/useCampaignValidation";
import {
  CampaignActionsDialog,
  CampaignActionsDropdown,
} from "../../CampaignActions";
import CampaignStateTag from "../../CampaignStateTag";
import WorkerAccounts from "../../WorkerAccounts";
import WorkerAccountsTag from "../../WorkerAccountsTag";

import type { CampaignActionType } from "../../../types";

export default function CampaignInfo() {
  const isDesktop = useTwBreakpoint("lg");
  const campaign = useCampaignContext();

  const {
    type,
    name,
    state,
    outreach_type: outreachType,
    owners,
    start_date: startDate,
    uses_connection_note: isUsingNote,
  } = campaign;
  const isDrip = outreachType === "drip";
  const { icon: SubtypeIcon } = CAMPAIGN_OUTREACH_OPTIONS[outreachType];
  const { label } = CAMPAIGN_TYPES[type];

  const [selectedActionType, setSelectedActionType] =
    useState<CampaignActionType>();
  const [isActionsModalOpen, setIsActionsModalOpen] = useState(false);

  function setAction(action: CampaignActionType) {
    setIsActionsModalOpen(true);
    setSelectedActionType(action);
  }

  const { hasInsufficientLicenses } = useCampaignValidation(owners);

  const workerAccounts = isDrip ? (
    <WorkerAccountsTag size="md" owners={owners} isUsingNote={isUsingNote} />
  ) : (
    <WorkerAccounts />
  );

  const stateTag = (
    <CampaignStateTag
      size="md"
      state={state}
      owners={owners}
      startDate={startDate}
      isUsingNote={isUsingNote}
    />
  );
  const typeAndSubtype = (
    <div className="flex items-center gap-x-1 text-black-500">
      <SubtypeIcon size={16} />
      <span className="line-clamp-1 break-all text-button-xs">
        {label} • {isDrip ? "1st" : "2nd & 3rd+"} connections
      </span>
    </div>
  );

  const startCampaignButton = (
    <RestrictedComponent visibleForPermissions={["campaigns_full"]}>
      <RenderIf condition={state === "PAUSED" || state === "SCHEDULED"}>
        <Button
          variant="default-purple"
          leftIcon={<Play weight="fill" />}
          size="sm"
          onClick={() =>
            setAction(hasInsufficientLicenses ? "license" : "start")
          }
        >
          Start
        </Button>
      </RenderIf>

      <RenderIf condition={state === "CREATED"}>
        <div className="group flex rounded-full bg-purple-500">
          <button
            type="button"
            className="flex grow items-center justify-center gap-2 rounded-l-full pl-3 pr-2 text-button-xs text-white transition-colors duration-300 hover:bg-purple-400"
            onClick={() =>
              setAction(hasInsufficientLicenses ? "license" : "start")
            }
          >
            Start campaign
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label="more-start-campaign-options"
              className={clsx(
                "group relative h-8 rounded-r-full p-2 transition-colors duration-300 data-[state=open]:bg-purple-400 hover:bg-purple-400",
                "before:absolute before:left-0 before:top-1 before:h-6 before:w-px before:bg-purple-400 before:opacity-100 before:transition-opacity before:duration-300 before:ease-in-out before:group-hover:opacity-0",
              )}
            >
              <CaretDown className="size-4 fill-white transition-transform group-data-[state=open]:rotate-180" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="z-aboveDialog" align="end">
              <DropdownMenuList>
                <DropdownMenuItem
                  onClick={() =>
                    setAction(hasInsufficientLicenses ? "license" : "schedule")
                  }
                >
                  <ClockCounterClockwise />
                  Schedule campaign
                </DropdownMenuItem>
              </DropdownMenuList>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </RenderIf>
    </RestrictedComponent>
  );
  const campaignTypeIcon = <CampaignTypeIcon type={type} isDrip={isDrip} />;
  const actionsDropdown = (
    <RestrictedComponent visibleForPermissions={["campaigns_full"]}>
      <CampaignActionsDropdown
        campaign={campaign}
        setAction={setAction}
        isDetailedView
      />
    </RestrictedComponent>
  );

  let mainContent: JSX.Element;
  if (isDesktop) {
    mainContent = (
      <>
        {campaignTypeIcon}

        <div className="ml-2.5">
          <div className="flex items-center gap-x-1.5">
            <h2 className="break-words text-md-bold">{name}</h2>
            {stateTag}
          </div>

          {typeAndSubtype}
        </div>

        <div className="flex flex-1 items-center justify-end gap-x-2.5">
          <div className="flex items-center gap-x-1">
            <span className="text-xs text-black-500">Run by:</span>
            {workerAccounts}
          </div>
          <span className="h-8 w-px bg-black-200 last:hidden" />
          {startCampaignButton}
          {actionsDropdown}
        </div>
      </>
    );
  } else {
    mainContent = (
      <>
        <div className="flex w-full gap-x-2.5">
          {campaignTypeIcon}

          <div className="flex grow flex-col gap-y-1">
            <h2 className="break-words text-md-bold">{name}</h2>
            {typeAndSubtype}
          </div>

          <span className="h-8 w-px bg-black-200 last:hidden" />
          {actionsDropdown}
        </div>

        {/* Divider */}
        <span className="my-2 h-px w-full bg-black-200" />

        <div className="flex w-full items-center justify-between gap-x-2">
          <div className="flex items-center gap-x-1">
            <span className="text-xs text-black-500">Run by:</span>
            {workerAccounts}
          </div>

          {stateTag}
        </div>
      </>
    );
  }

  return (
    <>
      <section className="flex flex-col items-start rounded-3xl border border-black-200 p-3 lg:flex-row lg:items-center">
        {mainContent}
      </section>

      <RenderIf condition={!isDesktop}>{startCampaignButton}</RenderIf>

      <CampaignActionsDialog
        isOpen={isActionsModalOpen}
        setIsOpen={setIsActionsModalOpen}
        actionType={selectedActionType}
        campaign={campaign}
      />
    </>
  );
}
